/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { useState, useMemo } from 'react';
import { TableManageViewsModal } from 'carbon-addons-iot-react';
import { useIntl, defineMessages } from 'react-intl';
import noop from 'lodash/noop';

import { getDescription } from 'tableUtils';
import { TableViewType, ManageViewsType } from 'TableTypes';

const messages = defineMessages({
  closeIconDescription: 'Close',
  defaultLabelText: 'Default',
  deleteIconText: 'Delete',
  deleteWarningTextTemplate: `You are about to delete view {label}.`,
  deleteWarningCancel: 'Cancel',
  deleteWarningConfirm: 'Delete',
  editIconText: 'Edit',
  listTitle: 'Available views',
  modalTitle: 'Manage views',
  privateLabelText: 'Private',
  publicLabelText: 'Public',
  publicCheckboxLabelText: 'Display public views',
  searchPlaceholderText: 'Search views',
  searchClearButtonLabelText: 'Clear search input',
  searchIconLabelText: 'Search',
  ascending: 'Ascending',
  descending: 'Descending',
});

type ManageViewModalProps = {
  isOpen?: boolean;
  onEdit?: (viewId: string) => void;
  onDelete?: (viewId: string) => void;
  onClose?: () => void;
  onClearError?: () => void;
  error?: string;
  isLoading?: boolean;
  tableViews: TableViewType[];
  currentUser: string;
};

const defaultProps = {
  isOpen: true,
  onEdit: noop,
  onDelete: noop,
  onClose: noop,
  onClearError: noop,
  error: null,
  isLoading: false,
};

const ManageViewsModal = ({
  isOpen = true,
  onEdit = noop,
  onDelete = noop,
  onClose = noop,
  onClearError = noop,
  error = null,
  isLoading = false,
  tableViews,
  currentUser,
}: ManageViewModalProps): JSX.Element => {
  const { formatMessage } = useIntl();

  // keeps track of which views to display based on public and search value in manage view modal
  const [filteredManageViews, setFilteredManageViews] = useState(() =>
    tableViews.filter(view => view.isPublic || view.userId === currentUser)
  );

  // determines whether public views are displayed or not
  const [showPublicViews, setShowPublicViews] = useState(true);

  // need to format the views so the modal can display the correct information
  // TODO: should we add a new access control permission for editing and deleting views?
  const manageViewModalViews: ManageViewsType[] = useMemo(
    () =>
      filteredManageViews?.map(manageView => ({
        ...manageView,
        description: getDescription(manageView, formatMessage),
        isEditable: true,
        isDeleteable: true,
      })),
    [filteredManageViews, formatMessage]
  );

  return (
    <TableManageViewsModal
      displayPublicDefaultChecked
      open={isOpen}
      testId="table-manage-views-modal"
      actions={{
        onDisplayPublicChange: (showPublic: boolean): void => {
          if (showPublic) {
            setFilteredManageViews(
              tableViews.filter(view => view.isPublic || view.userId === currentUser)
            );
            setShowPublicViews(showPublic);
          } else {
            setFilteredManageViews(
              tableViews.filter(view => !view.isPublic && view.userId === currentUser)
            );
            setShowPublicViews(showPublic);
          }
        },
        onSearchChange: (searchTerm = ''): void => {
          if (searchTerm === '') {
            if (showPublicViews) {
              setFilteredManageViews(
                tableViews.filter(view => view.isPublic || view.userId === currentUser)
              );
            } else {
              setFilteredManageViews(
                tableViews.filter(view => !view.isPublic && view.userId === currentUser)
              );
            }
          } else {
            const filteredViews = filteredManageViews.filter(filteredView =>
              filteredView.title.toLowerCase().includes(searchTerm)
            );
            setFilteredManageViews(filteredViews);
          }
        },
        onEdit,
        onDelete: (viewIdToDelete): void => {
          // First delete this modals current views
          setFilteredManageViews(manageViews =>
            manageViews.filter(view => view.id !== viewIdToDelete)
          );
          // Then send to the API to delete the view
          onDelete(viewIdToDelete);
        },
        onClearError,
        onClose,
      }}
      defaultViewId={tableViews.filter(view => view.isDefault)[0]?.id}
      error={error}
      isLoading={isLoading}
      views={manageViewModalViews}
      i18n={{
        closeIconDescription: formatMessage(messages.closeIconDescription),
        defaultLabelText: formatMessage(messages.defaultLabelText),
        deleteIconText: formatMessage(messages.deleteIconText),
        deleteWarningTextTemplate: (label: string): string =>
          formatMessage(messages.deleteWarningTextTemplate, { label }),
        deleteWarningCancel: formatMessage(messages.deleteWarningCancel),
        deleteWarningConfirm: formatMessage(messages.deleteWarningConfirm),
        editIconText: formatMessage(messages.editIconText),
        listTitle: formatMessage(messages.listTitle),
        modalTitle: formatMessage(messages.modalTitle),
        privateLabelText: formatMessage(messages.privateLabelText),
        publicLabelText: formatMessage(messages.publicLabelText),
        publicCheckboxLabelText: formatMessage(messages.publicCheckboxLabelText),
        searchPlaceholderText: formatMessage(messages.searchPlaceholderText),
        searchClearButtonLabelText: formatMessage(messages.searchClearButtonLabelText),
        searchIconLabelText: formatMessage(messages.searchIconLabelText),
      }}
    />
  );
};

ManageViewsModal.defaultProps = defaultProps;
export default ManageViewsModal;
